/* eslint-disable */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import '../../components/skeletonRow.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faDownload,
  faSync,
  faChevronLeft,
  faChevronRight,
  faCalendarAlt,
} from '@fortawesome/free-solid-svg-icons';
import { API_URL } from '../../config';
import { showToast } from '../../components/toast';
import DistributorReportStats from './DistributorReportStats';
import DistributorReportCharts from './DistributorReportCharts';
import DistributorSummaryTable from './DistributorSummaryTable';

const defaultDateRange = {
  startDate: moment().subtract(29, 'days').format('YYYY-MM-DD'),
  endDate: moment().format('YYYY-MM-DD'),
};

const DistributorReport = () => {
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Date Range Filter State (Top Right)
  const [dateRange, setDateRange] = useState(defaultDateRange);

  // Column Filters for Summary Table
  const [summaryColumnFilters, setSummaryColumnFilters] = useState({
    name: '',
    referralCode: '',
    mobile: '',
    email: '',
  });

  // Summary Data (KPIs & Charts)
  const [summaryData, setSummaryData] = useState({ kpi: {}, charts: {} });

  // Partner Summary Table Data
  const [summaryTableData, setSummaryTableData] = useState([]);
  const [summaryTableTotal, setSummaryTableTotal] = useState(0);
  const [summaryTablePage, setSummaryTablePage] = useState(1);
  const [summaryTableOrderBy, setSummaryTableOrderBy] = useState('grossBusiness');
  const [summaryTableOrderDir, setSummaryTableOrderDir] = useState('DESC');

  // Fetch KPI & Charts Summary Data
  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('authToken');
      const payload = {
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
      };

      const res = await axios.post(`${API_URL}report-distributor/summary`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data?.success) {
        setSummaryData(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch summary data', err);
      showToast.error('Failed to load summary analytics.');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  // Fetch Partner Summary Table (Grouped by Distributor, 1 row per distributor)
  const fetchSummaryTable = useCallback(
    async (
      page = 1,
      orderBy = summaryTableOrderBy,
      orderDir = summaryTableOrderDir,
      colFilters = summaryColumnFilters
    ) => {
      try {
        const token = localStorage.getItem('authToken');
        const searchTerms = [
          colFilters.name,
          colFilters.referralCode,
          colFilters.mobile,
          colFilters.email,
        ]
          .filter(Boolean)
          .join(' ');

        const payload = {
          startDate: dateRange.startDate || undefined,
          endDate: dateRange.endDate || undefined,
          search: searchTerms || undefined,
          page,
          limit: 20,
          orderBy,
          orderDir,
        };

        const res = await axios.post(`${API_URL}report-distributor/distributors-table`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data?.success) {
          setSummaryTableData(res.data.data.list || []);
          setSummaryTableTotal(res.data.data.total || 0);
          setSummaryTablePage(page);
        }
      } catch (err) {
        console.error('Failed to fetch partner summary table', err);
      }
    },
    [dateRange, summaryTableOrderBy, summaryTableOrderDir, summaryColumnFilters]
  );

  // Initial & Dependency Load
  useEffect(() => {
    fetchSummary();
    fetchSummaryTable(1);
  }, [fetchSummary, fetchSummaryTable]);

  // Handle Date Change in Top Right
  const handleDateChange = (field, value) => {
    setDateRange((prev) => ({ ...prev, [field]: value }));
  };

  // Clear Filters Handler
  const handleClearFilters = () => {
    setDateRange(defaultDateRange);
    setSummaryColumnFilters({ name: '', referralCode: '', mobile: '', email: '' });
    fetchSummaryTable(1, 'grossBusiness', 'DESC', { name: '', referralCode: '', mobile: '', email: '' });
  };

  const hasAnyFilter = () => {
    return (
      summaryColumnFilters.name ||
      summaryColumnFilters.referralCode ||
      summaryColumnFilters.mobile ||
      summaryColumnFilters.email
    );
  };

  // Export CSV Handler
  const handleExportCSV = async () => {
    try {
      setExportLoading(true);
      const token = localStorage.getItem('authToken');
      const payload = {
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
      };

      const res = await axios.post(`${API_URL}report-distributor/export`, payload, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `distributor_report_${moment().format('YYYYMMDD_HHmm')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast.success('Distributor report exported successfully!');
    } catch (err) {
      console.error('Export error', err);
      showToast.error('Failed to export report.');
    } finally {
      setExportLoading(false);
    }
  };

  // Pagination Details
  const totalPages = Math.max(1, Math.ceil(summaryTableTotal / 20));
  const startItem = summaryTableTotal === 0 ? 0 : (summaryTablePage - 1) * 20 + 1;
  const endItem = Math.min(summaryTablePage * 20, summaryTableTotal);
  const showingRecords = `${startItem} to ${endItem} of ${summaryTableTotal}`;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchSummaryTable(newPage);
    }
  };

  const refreshData = () => {
    fetchSummary();
    fetchSummaryTable(summaryTablePage);
  };

  return (
    <div id="page-container" className="page-header-fixed page-sidebar-fixed fade in">
      <div id="content">
        {/* Top Header with Breadcrumbs on Left and From-To Date Range on Top Right */}
        <div className="row pb-3 align-items-center">
          <div className="col-md-5">
            <ul className="breadcrumb" style={{ margin: 0, padding: 0, background: 'transparent' }}>
              <li className="breadcrumb-item">
                <a href="/dashboard" style={{ textDecoration: 'none' }}>
                  <FontAwesomeIcon icon={faHome} /> Home
                </a>
              </li>
              <li className="breadcrumb-item">Reports</li>
              <li className="breadcrumb-item active">Distributor Report</li>
            </ul>
          </div>

          {/* Top Right Date Range Filter */}
          <div className="col-md-7 d-flex justify-content-end align-items-center gap-2">
            <div style={{ display: 'flex', alignItems: 'center', background: '#fff', border: '1px solid #ced4da', borderRadius: '4px', padding: '3px 8px', gap: '6px' }}>
              <FontAwesomeIcon icon={faCalendarAlt} style={{ color: '#6c757d', fontSize: '12px' }} />
              <span style={{ fontSize: '11.5px', fontWeight: '600', color: '#495057' }}>From:</span>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => handleDateChange('startDate', e.target.value)}
                style={{ border: 'none', outline: 'none', fontSize: '12px', background: 'transparent', padding: '0 2px' }}
              />
              <span style={{ fontSize: '11.5px', fontWeight: '600', color: '#495057' }}>To:</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => handleDateChange('endDate', e.target.value)}
                style={{ border: 'none', outline: 'none', fontSize: '12px', background: 'transparent', padding: '0 2px' }}
              />
            </div>

            <button
              className="btn btn-default btn-sm"
              onClick={refreshData}
              title="Apply Date Filter & Refresh"
            >
              <FontAwesomeIcon icon={faSync} />
            </button>
          </div>
        </div>

        {/* 8 Summary Metric Widgets matching Email Statistics Report */}
        <DistributorReportStats kpi={summaryData.kpi} loading={loading} />

        {/* Visual Analytics Charts (Full Width Trend + 2 Side-by-Side Charts) */}
        <DistributorReportCharts charts={summaryData.charts} loading={loading} />

        {/* Action & Control Bar */}
        <div className="panel-heading d-flex justify-content-between align-items-center" style={{ padding: '8px 0', borderBottom: 'none' }}>
          {/* Left: Table Title & Clear Filters */}
          <div className="d-flex align-items-center gap-2">
            <h4 className="panel-title" style={{ fontSize: '14px', fontWeight: '600', margin: 0 }}>
              Distributor Performance Summary
            </h4>

            {hasAnyFilter() && (
              <button
                className="btn btn-default btn-xs"
                style={{ marginLeft: '10px' }}
                onClick={handleClearFilters}
                title="Clear all filters"
              >
                Clear Filter
              </button>
            )}
          </div>

          {/* Right: Record status count, refresh, export, pagination */}
          <div className="d-flex align-items-center">
            <span style={{ fontSize: '0.8rem' }} className="me-3">
              {loading ? (
                <span className="skeleton-text" style={{ width: '140px', display: 'inline-block' }}></span>
              ) : (
                showingRecords
              )}
            </span>

            <button
              className="btn btn-default btn-sm mx-1"
              onClick={refreshData}
              title="Refresh Data"
            >
              <FontAwesomeIcon icon={faSync} />
            </button>

            <button
              className="btn btn-default btn-sm mx-1"
              onClick={handleExportCSV}
              disabled={exportLoading || summaryTableTotal === 0}
              title="Export Report"
            >
              {exportLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Exporting...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faDownload} /> Export Report
                </>
              )}
            </button>

            <button
              className={`btn btn-default btn-sm ${summaryTablePage === 1 ? 'd-none' : ''}`}
              title="Previous"
              onClick={() => handlePageChange(summaryTablePage - 1)}
              disabled={summaryTablePage === 1}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            &nbsp;
            <button
              className={`btn btn-default btn-sm ${summaryTablePage >= totalPages ? 'd-none' : ''}`}
              title="Next"
              onClick={() => handlePageChange(summaryTablePage + 1)}
              disabled={summaryTablePage >= totalPages}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </div>

        {/* Single Consolidated Table (1 Row Per Distributor with Inline Filters & Clickable Link) */}
        <DistributorSummaryTable
          data={summaryTableData}
          page={summaryTablePage}
          limit={20}
          loading={loading}
          orderBy={summaryTableOrderBy}
          orderDir={summaryTableOrderDir}
          columnFilters={summaryColumnFilters}
          onColumnFilterChange={(col, val) =>
            setSummaryColumnFilters((prev) => ({ ...prev, [col]: val }))
          }
          onApplyFilters={() => fetchSummaryTable(1, summaryTableOrderBy, summaryTableOrderDir, summaryColumnFilters)}
          onSortChange={(col, dir) => {
            setSummaryTableOrderBy(col);
            setSummaryTableOrderDir(dir);
            fetchSummaryTable(1, col, dir, summaryColumnFilters);
          }}
        />
      </div>
    </div>
  );
};

export default DistributorReport;
